import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  DatePicker,
  Select,
  Button,
  Spin,
  Divider,
  Tabs,
} from "antd";
import {
  UserOutlined,
  RiseOutlined,
  LoginOutlined,
  UserDeleteOutlined,
  DollarOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import useRequest from "../hooks/useRequest";
import * as XLSX from "xlsx";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || {};

const { Title, Text } = Typography;
const { TabPane } = Tabs;

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
);


const MOCK_DATA = {
  user: {
    metrics: {
      total_users: 0,
      active_users: 0,
      inactive_users: 0,
      new_user: 0,
    },
    user_growth: [
      {},
    ],
  },
  transaction: {
    metrics: {
      total_transactions: 0,
      total_transaction_value: 0,
    },
    transaction_trend: {
      daily: 0,
      weekly: 0,
      monthly: 0
    },
    breakdown: {
      WITHDRAWAL: 0,
      ADMIN_CREDIT: 0,
      DEPOSIT: 0,
      WALLET_TRANSFER: 0
    }
  },
  kyc: {
    metrics: {
      completed: 0,
      pending: 0,
      rejected: 0,
      completion_rate: 0,
    },
    rejection_reasons: {
      "Face Mismatch": 0,
      "Others": 0,
      "Recaptured Image": 0,
      "Tampered Document": 0,
    },
  },

  referral: {
    metrics: {
      users_used_referral: 0,
      total_referral_registration: 0,
      cashback_distributed: 0,
      average_cashback: 0,
    },
    growth: {
      daily_data: [],
      monthly_data: []
    }
  },
};

/* ---------------- KPI CARD ---------------- */

const KPI = ({ icon, title, value, color = "#1677ff" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -6 }}
    transition={{ duration: 0.4 }}
  >
    <Card
      bordered={false}
      style={{
        borderRadius: 18,
        height: 140,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
        transition: "all 0.3s ease",
      }}
      bodyStyle={{ padding: 24 }}
      hoverable
    >
      {/* Left Accent */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: color,
        }}
      />

      <Space direction="vertical">
        <div style={{ fontSize: 20, color }}>{icon}</div>

        <Text type="secondary" style={{ fontWeight: 500 }}>
          {title}
        </Text>

        <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </Title>
      </Space>
    </Card>
  </motion.div>
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12,
          weight: "500",
        },
      },
    },
    tooltip: {
      backgroundColor: "#111827",
      padding: 12,
      cornerRadius: 8,
      titleFont: { size: 14, weight: "600" },
      bodyFont: { size: 13 },
    },
  },
  scales: {
    x: {
      grid: { display: false },
    },
    y: {
      grid: {
        color: "rgba(0,0,0,0.05)",
      },
      ticks: {
        font: { size: 12 },
      },
    },
  },
};

/* ---------------- MAIN COMPONENT ---------------- */

const ReportsAnalytics = () => {
  const { request } = useRequest();
  const [loading] = useState(false);
  const [period, setPeriod] = useState("7days");
  const [activeTab, setActiveTab] = useState("user");
  const [dateRange, setDateRange] = useState([]);
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportType, setExportType] = useState("selected");
  const [data, setData] = useState(MOCK_DATA);
  const [growthPeriod, setGrowthPeriod] = useState("7days");
  const [transactionType, setTransactionType] = useState([]);
  const [selectedTransactionType, setSelectedTransactionType] = useState(undefined);
  /* ---- Persist Active Tab ---- */
  const fetchData = useCallback(() => {
    const params = new URLSearchParams();
    // params.append("tab", activeTab);
    // params.append("period", period);
    const start_date = dateRange?.[0]?.format?.("YYYY-MM-DD");
    const end_date = dateRange?.[1]?.format?.("YYYY-MM-DD");
    if (start_date) params.append("start_date", start_date);
    if (end_date) params.append("end_date", end_date);
    // params.append("exportFormat", exportFormat);
    if (activeTab === "transaction" && selectedTransactionType) {
      params.append("service_type", selectedTransactionType);
    }
    request({
      url: `api/admin/reports/${activeTab}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setData((prev) => {
          return {
            ...prev,
            [activeTab]: data?.data,
          };
        });
        if (activeTab === "transaction" && data?.data?.breakdown && typeof data?.data?.breakdown === "object") {
          const transactionTypes = Object.keys(data?.data?.breakdown);
          setTransactionType(transactionTypes);
        }
      },
    });
  }, [activeTab, dateRange, exportFormat, period, selectedTransactionType]);

  useEffect(() => {
    // const savedTab = localStorage.getItem("analytics_active_tab");
    fetchData();
    // if (savedTab) setActiveTab(savedTab);
  }, [fetchData]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    localStorage.setItem("analytics_active_tab", key);
  };

  const metricMapByTab = useMemo(
    () => ({
      user: [
        ["total_users", data.user.metrics.total_users],
        ["active_users", data.user.metrics.active_users],
        ["inactive_users", data.user.metrics.inactive_users],
        ["new_users", data.user.metrics.new_user],
      ],
      transaction: [
        ["total_transactions_daily", data.transaction.transaction_trend.daily],
        ["total_transactions_weekly", data.transaction.transaction_trend.weekly],
        ["total_transactions_monthly", data.transaction.transaction_trend.monthly],
        ["total_transaction_value", data.transaction.metrics.total_transaction_value],
        ["wallet_credits", data.transaction.breakdown.DEPOSIT],
        ["wallet_debits", data.transaction.breakdown.WITHDRAWAL],
        ["topups", data.transaction.breakdown.DEPOSIT],
        ["withdrawals", data.transaction.breakdown.WITHDRAWAL],
      ],
      kyc: [
        ["kyc_completed", data.kyc.metrics.completed],
        ["kyc_pending", data.kyc.metrics.pending],
        ["kyc_rejected", data.kyc.metrics.rejected],
        ["kyc_completion_rate_percent", data.kyc.metrics.completion_rate],
      ],
      referral: [
        ["users_used_referral", data.referral.metrics.users_used_referral],
        ["total_referral_registrations", data.referral.metrics.total_referral_registration],
        ["cashback_distributed", data.referral.metrics.cashback_distributed],
        ["avg_cashback", data.referral.metrics.average_cashback],
      ],
    }),
    [data],
  );

  const formatMetricLabel = (value) =>
    String(value || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getTabExportRows = useCallback(
    (tabKey) => {
      const rows = metricMapByTab[tabKey] || [];
      if (tabKey === "transaction" && exportType === "filtered" && selectedTransactionType) {
        return rows.filter(([metricKey]) =>
          metricKey.toLowerCase().includes(selectedTransactionType.toLowerCase()),
        );
      }
      return rows;
    },
    [metricMapByTab, exportType, selectedTransactionType],
  );

  const exportTables = useMemo(() => {
    const tabs = exportType === "summary" ? ["user", "transaction", "kyc", "referral"] : [activeTab];
    const tabNameMap = {
      user: "User Analytics",
      transaction: "Transaction Analytics",
      kyc: "KYC Analytics",
      referral: "Referral Analytics",
    };
    return tabs.map((tab) => ({
      title: tabNameMap[tab],
      headers: getTabExportRows(tab).map(([metric]) => formatMetricLabel(metric)),
      values: getTabExportRows(tab).map(([, value]) => value ?? 0),
    }));
  }, [activeTab, exportType, getTabExportRows]);

  const exportMetaTable = useMemo(() => {
    const fromDate = dateRange?.[0]?.format?.("YYYY-MM-DD") || "N/A";
    const toDate = dateRange?.[1]?.format?.("YYYY-MM-DD") || "N/A";
    return {
      headers: ["Active Report", "Start Date", "End Date", "Transaction Filter"],
      values: [
        // formatMetricLabel(exportType),
        formatMetricLabel(activeTab),
        fromDate,
        toDate,
        selectedTransactionType || "All",
      ],
    };
  }, [activeTab, dateRange, exportType, selectedTransactionType]);

  const combinedExportTable = useMemo(() => {
    const metricHeaders = exportTables.flatMap((table) => table.headers || []);
    const metricValues = exportTables.flatMap((table) => table.values || []);
    return {
      headers: [...exportMetaTable.headers, ...metricHeaders],
      values: [...exportMetaTable.values, ...metricValues],
    };
  }, [exportMetaTable, exportTables]);

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildCsvContent = () => {
    const csvRows = [combinedExportTable.headers, combinedExportTable.values];
    return csvRows
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
  };

  const exportToExcel = (baseFileName) => {
    const workbook = XLSX.utils.book_new();
    const sheetRows = combinedExportTable.headers.length
      ? [combinedExportTable.headers, combinedExportTable.values]
      : [["No data"], ["-"]];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(sheetRows), "Reports Export");
    XLSX.writeFile(workbook, `${baseFileName}.xlsx`);
  };

  const exportToPdf = (baseFileName) => {
    const content = [
      { text: "Reports & Analytics Export", style: "header" },
      {
        table: {
          headerRows: 1,
          widths: combinedExportTable.headers.map(() => "auto"),
          body: [combinedExportTable.headers, combinedExportTable.values],
        },
        layout: "lightHorizontalLines",
      },
    ];

    pdfMake.createPdf({
      pageSize: "A4",
      pageMargins: [24, 24, 24, 24],
      content,
      styles: {
        header: { fontSize: 14, bold: true, margin: [0, 0, 0, 10] },
        subHeader: { fontSize: 11, bold: true, margin: [0, 10, 0, 4] },
      },
    }).download(`${baseFileName}.pdf`);
  };

  const handleExport = () => {
    const fromDate = dateRange?.[0]?.format?.("YYYYMMDD") || "all";
    const toDate = dateRange?.[1]?.format?.("YYYYMMDD") || "all";
    const baseFileName = `report_analytics_${exportType}_${activeTab}_${fromDate}_${toDate}`;

    if (exportFormat === "excel") {
      exportToExcel(baseFileName);
      return;
    }

    if (exportFormat === "pdf") {
      exportToPdf(baseFileName);
      return;
    }

    const csvBlob = new Blob([buildCsvContent()], { type: "text/csv;charset=utf-8;" });
    downloadBlob(csvBlob, `${baseFileName}.csv`);
  };

  /* ---------------- USER CHARTS ---------------- */

  const userGrowthChart = {
    labels: data.user.user_growth.map((d) => d.label),
    datasets: [
      {
        label: "User Growth",
        data: data.user.user_growth.map((d) => d.value),
        borderColor: "#1677ff",
        backgroundColor: "rgba(22,119,255,0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const userActivityChart = {
    labels: ["Active", "Inactive"],
    datasets: [
      {
        data: [data.user.metrics.active_users, data.user.metrics.inactive_users],
        backgroundColor: ["#52c41a", "#ff4d4f"],
      },
    ],
  };

  /* ---------------- TRANSACTION CHARTS ---------------- */

  const transactionTrendChart = {
    labels: ["Daily", "Weekly", "Monthly"],
    datasets: [
      {
        label: "Transactions",
        data: [
          data.transaction.transaction_trend.daily,
          data.transaction.transaction_trend.weekly,
          data.transaction.transaction_trend.monthly,
        ],
        borderColor: "#1677ff",
        backgroundColor: ["#1677ff", "#722ed1", "#faad14", "#ff4d4f"],
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const transactionBreakdownChart = {
    labels: ["Admin Credit", "Deposit", "Wallet Transfer", "Withdrawal"],
    datasets: [
      {
        data: [
          data.transaction.breakdown.ADMIN_CREDIT,
          data.transaction.breakdown.DEPOSIT,
          data.transaction.breakdown.WALLET_TRANSFER,
          data.transaction.breakdown.WITHDRAWAL,
        ],
        backgroundColor: ["#1677ff", "#722ed1", "#faad14", "#ff4d4f"],
      },
    ],
  };

  const referralGrowth = data.referral.growth || {};
  const referralGrowthData =
    growthPeriod === "7days"
      ? referralGrowth?.daily_data || []
      : referralGrowth?.monthly_data || [];

  /* ---------------- REFERRAL CHARTS ---------------- */
  const referralGrowthChart = {
    labels: referralGrowthData.map((d) => d.month || d.date),
    datasets: [
      {
        label: "Referral Growth",
        data: referralGrowthData.map((d) => d.users),
        borderColor: "#1677ff",
        backgroundColor: "rgba(22,119,255,0.2)",
      },
    ],
  }

  /* ---------------- UI ---------------- */

  return (
    <div
      style={{
        background: "linear-gradient(180deg,#f5f7fa 0%, #eef2f7 100%)",
        minHeight: "100vh",
      }}
    >
      <Card
        style={{
          borderRadius: 20,
          marginBottom: 30,
        }}
        bodyStyle={{ padding: 20 }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md="auto">
            <Title level={3} style={{ color: "#fff", margin: 0 }}>
              Reports & Analytics
            </Title>
            <Text style={{ color: "#94a3b8" }}>
              Platform insights & operational metrics overview
            </Text>
          </Col>

          <Col xs={24} md="auto">
            <Space wrap>
              <DatePicker.RangePicker
                size="middle"
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [])}
              />
              <Select
                placeholder="Export Format"
                style={{ minWidth: 150 }}
                value={exportFormat}
                onChange={setExportFormat}
              >
                <Select.Option value="csv">Export CSV</Select.Option>
                <Select.Option value="excel">Export Excel</Select.Option>
                <Select.Option value="pdf">Export PDF</Select.Option>
              </Select>
              {/* <Select
                placeholder="Export Type"
                style={{ minWidth: 210 }}
                value={exportType}
                onChange={setExportType}
              >
                <Select.Option value="summary">Export dashboard summary</Select.Option>
                <Select.Option value="selected">Export selected report</Select.Option>
                <Select.Option value="filtered">Export filtered data only</Select.Option>
                <Select.Option value="dateRange">Date-range based export</Select.Option>
              </Select> */}
              <Button type="primary" onClick={handleExport}>
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      {activeTab === "transaction" && <div style={{ padding: "0  10px" }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col xs={24}>
            <Space wrap>
              {/* <Select placeholder="User Type" style={{ minWidth: 140 }}>
                <Select.Option value="individual">Individual</Select.Option>
                <Select.Option value="premium">Premium</Select.Option>
                <Select.Option value="business">Business</Select.Option>
              </Select> */}

              <Select
                placeholder="Transaction Type"
                style={{ minWidth: 160 }}
                value={selectedTransactionType}
                onChange={(value) => setSelectedTransactionType(value)}
                allowClear
              >
                {transactionType.map((type) => (
                  <Select.Option key={type} value={type}>
                    {type.replaceAll("_", " ")}
                  </Select.Option>
                ))}
                {/* <Select.Option value="credit">Wallet Credit</Select.Option>
                <Select.Option value="debit">Wallet Debit</Select.Option>
                <Select.Option value="topup">Top-Up</Select.Option>
                <Select.Option value="withdrawal">Withdrawal</Select.Option> */}
              </Select>

              {/* <Select placeholder="KYC Status" style={{ minWidth: 140 }}>
                <Select.Option value="completed">Completed</Select.Option>
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="rejected">Rejected</Select.Option>
              </Select> */}
            </Space>
          </Col>
          {/* <Space style={{ marginTop: 20 }}>
          <Select placeholder="Export Format" style={{ width: 150 }}>
            <Select.Option value="csv">Export CSV</Select.Option>
            <Select.Option value="excel">Export Excel</Select.Option>
            <Select.Option value="pdf">Export PDF</Select.Option>
          </Select>

          <Select placeholder="Export Type" style={{ width: 200 }}>
            <Select.Option value="summary">Dashboard Summary</Select.Option>
            <Select.Option value="selected">Current Tab</Select.Option>
            <Select.Option value="filtered">Filtered Data Only</Select.Option>
          </Select>
        </Space> */}
        </Row>
      </div>}

      <Divider />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        size="large"
        style={{
          background: "#fff",
          padding: 20,
          borderRadius: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
        }}
        tabBarStyle={{
          fontWeight: 600,
        }}
      >
        {/* ================= USERS TAB ================= */}
        <TabPane tab="User Analytics" key="user">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <KPI
                icon={<UserOutlined />}
                title="Total Users"
                value={data.user.metrics.total_users}
                color="#1677ff"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <KPI
                icon={<LoginOutlined />}
                title="Active Users"
                value={data.user.metrics.active_users}
                color="#52c41a"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <KPI
                icon={<UserDeleteOutlined />}
                title="Inactive Users"
                value={data.user.metrics.inactive_users}
                color="#ff4d4f"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <KPI
                icon={<RiseOutlined />}
                title={`New Users (${period})`}
                value={data.user.metrics.new_user}
                color="#722ed1"
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={14}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  title="User Growth Trend"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    <Line data={userGrowthChart} options={chartOptions} />
                  </div>
                </Card>
              </motion.div>
            </Col>

            <Col xs={24} lg={10}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  title="User Activity Distribution"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    <Doughnut
                      data={userActivityChart}
                      options={{
                        ...chartOptions,
                        cutout: "75%",
                      }}
                    />
                  </div>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </TabPane>

        {/* ================= TRANSACTIONS TAB ================= */}
        <TabPane tab="Transaction Analytics" key="transaction">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={6}>
              <KPI
                icon={<SwapOutlined />}
                title={`Total Transactions (${period})`}
                value={data.transaction.metrics.total_transactions}
              />
            </Col>

            <Col xs={24} md={12} lg={6}>
              <KPI
                icon={<DollarOutlined />}
                title="Total Transaction Value"
                value={`NZ$ ${data.transaction.metrics.total_transaction_value.toLocaleString()}`}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={14}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                {/* <Card
                  title="Transaction Trend"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    <Line data={transactionTrendChart} options={chartOptions} />
                  </div>
                </Card> */}
                <Col xs={24}>
                  <Card title="Transaction Overview" style={{ borderRadius: 16 }}>
                    <div style={{ height: 300 }}>
                      <Bar data={transactionTrendChart} options={chartOptions} />
                    </div>
                  </Card>
                </Col>
              </motion.div>
            </Col>

            <Col xs={24} lg={10}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  title="Transaction Breakdown"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    <Doughnut
                      data={transactionBreakdownChart}
                      options={{
                        ...chartOptions,
                        cutout: "75%",
                      }}
                    />
                  </div>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="KYC Analytics" key="kyc">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <KPI title="Completed" value={data.kyc.metrics.completed} />
            </Col>
            <Col xs={24} md={6}>
              <KPI title="Pending" value={data.kyc.metrics.pending} />
            </Col>
            <Col xs={24} md={6}>
              <KPI title="Rejected" value={data.kyc.metrics.rejected} />
            </Col>
            <Col xs={24} md={6}>
              <KPI
                title="Completion Rate (%)"
                value={`${data.kyc.metrics.completion_rate}%`}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  title="KYC Status Distribution"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    <Doughnut
                      data={{
                        labels: ["Completed", "Pending", "Rejected"],
                        datasets: [
                          {
                            data: [
                              data.kyc.metrics.completed,
                              data.kyc.metrics.pending,
                              data.kyc.metrics.rejected,
                            ],
                            backgroundColor: ["#52c41a", "#faad14", "#ff4d4f"],
                          },
                        ],
                      }}
                      options={{
                        ...chartOptions,
                        cutout: "75%",
                      }}
                    />
                  </div>
                </Card>
              </motion.div>
            </Col>

            <Col xs={24} lg={12}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  title="Rejection Reasons"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    <Doughnut
                      data={{
                        labels: Object.keys(data.kyc.rejection_reasons),
                        datasets: [
                          {
                            data: Object.values(data.kyc.rejection_reasons),
                            backgroundColor: [
                              "#1677ff",
                              "#722ed1",
                              "#faad14",
                              "#ff4d4f",
                            ],
                          },
                        ],
                      }}
                      options={{
                        ...chartOptions,
                        cutout: "75%",
                      }}
                    />
                  </div>
                </Card>
              </motion.div>
            </Col>
          </Row>

          <Divider />
          <Text>
            Average Verification Time: <b>{data.kyc.average_verification_time}</b>
          </Text>
        </TabPane>
        <TabPane tab="Referral & Cashback Analytics" key="referral">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <KPI
                title="Users Used Referral"
                value={data.referral.metrics.users_used_referral}
              />
            </Col>

            <Col xs={24} md={6}>
              <KPI
                title="Total Referral Registrations"
                value={data.referral.metrics.total_referral_registration}
              />
            </Col>

            <Col xs={24} md={6}>
              <KPI
                title="Cashback Distributed"
                value={`NZ$ ${data.referral.metrics.cashback_distributed.toLocaleString()}`}
              />
            </Col>

            <Col xs={24} md={6}>
              <KPI
                title="Avg Cashback Per User"
                value={`NZ$ ${data.referral.metrics.average_cashback}`}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <Card
                  title="Top Performing Referral Codes"
                  hoverable
                  style={{
                    borderRadius: 24,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ height: 350 }}>
                    {/* <Doughnut
                      data={{
                        labels: data.referrals.top_codes.map((c) => c.code),
                        datasets: [
                          {
                            data: data.referrals.top_codes.map((c) => c.users),
                            backgroundColor: ["#1677ff", "#13c2c2", "#722ed1"],
                          },
                        ],
                      }}
                      options={{
                        ...chartOptions,
                        cutout: "75%",
                      }}
                    /> */}
                    <Select
                      placeholder="Select Period"
                      style={{ minWidth: 150 }}
                      value={growthPeriod}
                      onChange={(value) => setGrowthPeriod(value)}
                    >
                      <Select.Option value="7days">7 Days</Select.Option>
                      <Select.Option value="monthly">Monthly</Select.Option>
                    </Select>
                    <div style={{ height: 250 }}>
                      <Line data={referralGrowthChart} options={chartOptions} />
                    </div>
                  </div>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ReportsAnalytics;
